import { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { profileKey } from "../lib/profileSession";
import type { WorkTarget } from "../lib/api-client/workReports";
import { StatefulButton } from "./beui/StatefulButton";

export function WorkDownload({ kind, id, onAccessLost }: WorkTarget & { onAccessLost: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false), [ready, setReady] = useState(false), [error, setError] = useState<string | null>(null);
  const key = `${profileKey(user)}:${kind}:${id}`, current = useRef(key), mounted = useRef(false), pending = useRef(false);
  current.current = key;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function download() {
    if (pending.current) return;
    const started = key;
    const assertCurrent = () => {
      if (!mounted.current || current.current !== started) throw new DOMException("Work selection changed during download.", "AbortError");
    };
    pending.current = true; setBusy(true); setReady(false); setError(null);
    try {
      await api.downloadWorkRecord({ kind, id }, assertCurrent);
      assertCurrent(); setReady(true);
    } catch (failure) {
      if (!mounted.current || current.current !== started || failure instanceof DOMException && failure.name === "AbortError") return;
      if (failure instanceof ApiError && (failure.status === 401 || failure.status === 404 || failure.status === 403
        && !["company_verification_required", "legal_reacceptance_required"].includes(failure.code || ""))) { onAccessLost(); return; }
      setError(failure instanceof ApiError ? failure.message : "Could not save the Work download. Please retry, or contact support if it keeps failing.");
    } finally {
      if (mounted.current && current.current === started) { pending.current = false; setBusy(false); }
    }
  }
  const name = busy ? "Preparing work download" : error ? "Retry work download" : "Download work record";
  return <div className="work-download space-y-2">
    <StatefulButton type="button" variant="outline" state={busy ? "loading" : error ? "error" : "idle"}
      aria-label={name} title={name} loadingText="Preparing…" errorText="Retry work record" iconPosition="start" icon={<Download className="h-4 w-4" aria-hidden="true" />}
      onClick={() => void download()} size="sm" className="rounded-full">Work record</StatefulButton>
    {error && <p role="alert" className="text-sm text-brand-red">{error}</p>}
    {ready && <p role="status" className="sr-only">Work download ready.</p>}
  </div>;
}
